import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { documentRepository, type LocChungTu } from '../../../lib/repositories/document.repository';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import type {
  BulkResult,
  DocDetail,
  DocPage,
  DocPatch,
  DocReprocessInput,
  DocStats,
  DocTranscript,
  DocUploadAccepted,
  DocUploadInput,
  TepXemTruoc,
} from '../types';

export const KHOA_CHUNG_TU = {
  goc: ['ocr', 'documents'] as const,
  danhSach: (loc: Omit<LocChungTu, 'cursor' | 'limit'>) => ['ocr', 'documents', 'danh-sach', loc] as const,
  thongKe: ['ocr', 'documents', 'thong-ke'] as const,
  chiTiet: (id: string) => ['ocr', 'documents', 'chi-tiet', id] as const,
  banChep: (id: string) => ['ocr', 'documents', 'ban-chep', id] as const,
  tep: (id: string, extra?: number) => ['ocr', 'documents', 'tep', id, extra ?? 'chinh'] as const,
};

export const MOT_TRANG_CHUNG_TU = 50;
/** Nhịp hỏi lại khi còn chứng từ đang quét: đủ thấy giai đoạn nhích, không dồn tải lên module. */
const NHIP_DANG_QUET = 4_000;

/**
 * Danh sách lọc ở **server** (`status`, `ocr_stage`, `type`, `search`), phân trang con trỏ bằng nút
 * `Tải thêm`. Còn dòng đang xử lý thì tự hỏi lại để nhãn giai đoạn nhích theo worker.
 */
export function useDanhSachChungTu(loc: Omit<LocChungTu, 'cursor' | 'limit'>) {
  return useInfiniteQuery<DocPage, ApiError>({
    queryKey: KHOA_CHUNG_TU.danhSach(loc),
    queryFn: ({ pageParam }) =>
      documentRepository.danhSach({ ...loc, limit: MOT_TRANG_CHUNG_TU, cursor: pageParam as string | undefined }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: thuLai,
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some((d) => d.status === 'PROCESSING')) ? NHIP_DANG_QUET : false,
  });
}

export function useThongKeChungTu() {
  return useQuery<DocStats, ApiError>({
    queryKey: KHOA_CHUNG_TU.thongKe,
    queryFn: documentRepository.thongKe,
    staleTime: 15_000,
    retry: thuLai,
    refetchInterval: (q) => ((q.state.data?.processing ?? 0) > 0 ? NHIP_DANG_QUET : false),
  });
}

/** Chi tiết; đang quét thì hỏi lại tới khi lượt quét kết thúc (thay cho SSE, xem README). */
export function useChiTietChungTu(id: string | undefined) {
  return useQuery<DocDetail, ApiError>({
    queryKey: KHOA_CHUNG_TU.chiTiet(id ?? ''),
    queryFn: () => documentRepository.chiTiet(id!),
    enabled: Boolean(id),
    staleTime: 10_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data?.status === 'PROCESSING' ? NHIP_DANG_QUET : false),
  });
}

export function useBanChepChungTu(id: string | undefined, bat: boolean) {
  return useQuery<DocTranscript, ApiError>({
    queryKey: KHOA_CHUNG_TU.banChep(id ?? ''),
    queryFn: () => documentRepository.banChep(id!),
    enabled: Boolean(id) && bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Tệp gốc để xem trước. Liên kết ký hết hạn nhanh nên không giữ lâu trong cache. */
export function useTepChungTu(id: string | undefined, extra?: number) {
  return useQuery<TepXemTruoc, ApiError>({
    queryKey: KHOA_CHUNG_TU.tep(id ?? '', extra),
    queryFn: () => documentRepository.tep(id!, extra),
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: thuLai,
  });
}

/**
 * Thao tác ghi trên chứng từ. Không tự bật toast: phía gọi (`useThaoTacChungTu`) quyết câu báo và
 * chỗ hiện lỗi. Xong việc nào cũng tải lại cả nhánh `ocr/documents` (danh sách, số liệu, chi tiết),
 * vì trạng thái một chứng từ đổi là thẻ số liệu đổi theo.
 */
export function useGhiChungTu() {
  const queryClient = useQueryClient();
  const lamMoi = () => void queryClient.invalidateQueries({ queryKey: KHOA_CHUNG_TU.goc });
  const ghiChiTiet = (d: DocDetail) => {
    queryClient.setQueryData(KHOA_CHUNG_TU.chiTiet(d.id), d);
    lamMoi();
  };

  return {
    taiLen: useMutation<DocUploadAccepted, ApiError, DocUploadInput>({
      mutationFn: (du) => documentRepository.taiLen(du),
      onSuccess: lamMoi,
    }),
    quetLai: useMutation<DocDetail, ApiError, { id: string; du: DocReprocessInput }>({
      mutationFn: ({ id, du }) => documentRepository.quetLai(id, du),
      onSuccess: ghiChiTiet,
    }),
    huyQuet: useMutation<DocDetail, ApiError, string>({
      mutationFn: (id) => documentRepository.huyQuet(id),
      onSuccess: ghiChiTiet,
    }),
    sua: useMutation<DocDetail, ApiError, { id: string; du: DocPatch }>({
      mutationFn: ({ id, du }) => documentRepository.sua(id, du),
      onSuccess: ghiChiTiet,
    }),
    xacNhan: useMutation<DocDetail, ApiError, string>({
      mutationFn: (id) => documentRepository.xacNhan(id),
      onSuccess: ghiChiTiet,
    }),
    chuyen: useMutation<DocDetail, ApiError, string>({
      mutationFn: (id) => documentRepository.chuyen(id),
      onSuccess: ghiChiTiet,
    }),
    xoa: useMutation<void, ApiError, string>({
      mutationFn: (id) => documentRepository.xoa(id),
      onSuccess: (_kq, id) => {
        queryClient.removeQueries({ queryKey: KHOA_CHUNG_TU.chiTiet(id) });
        lamMoi();
      },
    }),
    hangLoat: useMutation<BulkResult, ApiError, { viec: 'xac-nhan' | 'chuyen' | 'xoa'; ids: string[] }>({
      mutationFn: ({ viec, ids }) => documentRepository.hangLoat(viec, ids),
      onSuccess: lamMoi,
    }),
  };
}
